// Party Surfaces (F7) — the global Review Queue: an ENTITY roll-up list with
// one row per portal account across the six party types (Buyer / Seller /
// Vendor / Broker / Brokerage / Tps). Each row aggregates that account's
// collection-review workload (awaiting review / overdue / pending recipient
// / returned) and clicks through to the entity's own Review Queue tab.
//
// Gated by Collection.Staging.ReviewAndFile (B1 + Admin), same as the
// item-level staging queue it rolls up.
//
// All filter + sort state is URL-persisted (PortalAccountsPage pattern) with
// defaults resolved at PARSE time — never a post-mount "set defaults" effect
// (the double-fetch root-cause class).

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Badge, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { LoadMoreFooter } from "@/components/layout/LoadMoreFooter";
import { InternalUserPicker } from "@/components/users/InternalUserPicker";
import {
  collectionReviewQueueApi,
  type ReviewQueueEntityRow,
  type ReviewQueueSortBy,
  type ReviewQueueStatusFilter,
} from "@/api/collectionReviewQueue";
import { collectionReviewQueueKeys } from "@/api/collectionReviewQueueKeys";
import { fallbackUserLabel, useInternalUserLabels } from "@/api/internalUsers";
import { formatRelativeTime } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";

const PAGE_SIZE = 50;

// Same page-local account-type list as PortalAccountsPage — deliberately NOT
// the shared MATERIAL_AUDIENCES (which also drives the signup-wizard picker).
const ACCOUNT_TYPES = [
  "Buyer",
  "Seller",
  "Vendor",
  "Broker",
  "Brokerage",
  "Tps",
] as const;

const STATUS_OPTIONS: ReadonlyArray<{
  value: ReviewQueueStatusFilter;
  label: string;
}> = [
  { value: "awaiting", label: "Awaiting review" },
  { value: "overdue", label: "Overdue" },
  { value: "pending", label: "Pending recipient" },
  { value: "returned", label: "Returned" },
];

const SORT_OPTIONS: ReadonlyArray<{ value: ReviewQueueSortBy; label: string }> =
  [
    { value: "mostOverdue", label: "Most overdue" },
    { value: "oldest", label: "Oldest overdue" },
    { value: "mostOutstanding", label: "Most outstanding" },
    { value: "lastActivity", label: "Last activity" },
  ];

function parseStatus(raw: string | null): ReviewQueueStatusFilter | "" {
  return STATUS_OPTIONS.some((o) => o.value === raw)
    ? (raw as ReviewQueueStatusFilter)
    : "";
}

function parseSortBy(raw: string | null): ReviewQueueSortBy {
  return SORT_OPTIONS.some((o) => o.value === raw)
    ? (raw as ReviewQueueSortBy)
    : "mostOverdue";
}

// Count-tile deep-link token appended as &rqTile= so the entity's Review
// Queue tab can pre-select the matching pile.
export type ReviewQueueTile = "awaiting" | "overdue" | "pending" | "returned";

// Per-account-type Review Queue tab routes. Null entityId (no resolved party
// record) falls back to the portal-account admin page.
const ENTITY_ROUTES: Record<string, (entityId: string) => string> = {
  Buyer: (id) => `/borrowers/${id}?tab=review-requests`,
  Seller: (id) => `/sellers/${id}?tab=review-requests`,
  Vendor: (id) => `/portfolio-admin/vendors/${id}?tab=portal-review`,
  Broker: (id) => `/brokers/${id}?tab=review-queue`,
  Brokerage: (id) => `/brokerages/${id}?tab=portal-review`,
  Tps: (id) => `/portfolio-admin/tps/${id}?tab=portal-review`,
};

export function reviewQueueRowUrl(
  row: Pick<ReviewQueueEntityRow, "accountType" | "entityId" | "portalUserId">,
  tile?: ReviewQueueTile,
): string {
  const build = row.entityId ? ENTITY_ROUTES[row.accountType] : undefined;
  const base =
    build && row.entityId
      ? build(row.entityId)
      : `/admin/portal-accounts/${row.portalUserId}`;
  if (!tile) return base;
  return `${base}${base.includes("?") ? "&" : "?"}rqTile=${tile}`;
}

// FE-derived triage severity: any overdue submission trumps everything;
// otherwise anything on staff's plate (awaiting review) or bounced back to
// the recipient (returned) needs action; else the account is up to date.
export function rowSeverity(
  r: Pick<
    ReviewQueueEntityRow,
    "overdueCount" | "awaitingReviewCount" | "returnedCount"
  >,
): { status: "error" | "review" | "archived"; label: string } {
  if (r.overdueCount > 0) return { status: "error", label: "Overdue" };
  if (r.awaitingReviewCount > 0 || r.returnedCount > 0)
    return { status: "review", label: "Action needed" };
  return { status: "archived", label: "Up to date" };
}

// A count cell that deep-links to the entity's Review Queue tab with the
// matching &rqTile= pile pre-selected. Zero counts render as muted text —
// never a dead link.
function CountCell({
  row,
  tile,
  count,
  sub,
}: {
  row: ReviewQueueEntityRow;
  tile: ReviewQueueTile;
  count: number;
  sub?: string;
}) {
  if (count === 0)
    return <span className="text-caption text-gray-mid">0</span>;
  return (
    <Link
      to={reviewQueueRowUrl(row, tile)}
      onClick={(e) => e.stopPropagation()}
      className="inline-block hover:text-gold-shadow"
    >
      <span className="font-semibold text-onyx">{count}</span>
      {sub && <span className="block text-caption text-gray-mid">{sub}</span>}
    </Link>
  );
}

export function GlobalReviewQueuePage() {
  useBreadcrumbs([{ label: "External Collection" }, { label: "Review Queue" }]);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { dateTime, tz } = useDateFormatter();

  // URL-derived filter + sort state, defaults resolved at parse time.
  const accountType = params.get("accountType") ?? "";
  const status = parseStatus(params.get("status"));
  const assignedToUserId = params.get("assignedToUserId") ?? "";
  const search = params.get("q") ?? "";
  const overdueOnly = params.get("overdueOnly") === "true";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const sortBy = parseSortBy(params.get("sortBy"));

  // Debounced search input — the typed text drives `q` in the URL after a
  // 300ms quiet period so each keystroke doesn't refire the list query.
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    setSearchInput(search);
  }, [search]);
  useEffect(() => {
    if (searchInput === search) return;
    const handle = setTimeout(() => {
      const merged = new URLSearchParams(params);
      if (searchInput) merged.set("q", searchInput);
      else merged.delete("q");
      setParams(merged, { replace: true });
    }, 300);
    return () => clearTimeout(handle);
    // params + setParams are stable across renders for the same URL; we
    // intentionally only react to the input + the resolved URL value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, search]);

  // The date inputs emit bare YYYY-MM-DD; widen them to explicit UTC day
  // bounds so the inclusive server comparisons cover the whole selected day
  // (a bare date binds to midnight, which would clip the `to` edge).
  const activityFrom = from ? `${from}T00:00:00.000Z` : undefined;
  const activityTo = to ? `${to}T23:59:59.999Z` : undefined;

  const keyParams = {
    accountType: accountType || undefined,
    status: status || undefined,
    assignedToUserId: assignedToUserId || undefined,
    search: search || undefined,
    overdueOnly: overdueOnly || undefined,
    activityFrom,
    activityTo,
    sortBy,
  };

  // Server-side pagination via useInfiniteQuery + LoadMoreFooter (the
  // staging queue's approach). The queryKey carries every filter/sort, so
  // changing any of them starts a fresh page-1 query.
  const list = useInfiniteQuery({
    queryKey: collectionReviewQueueKeys.entities(keyParams),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      collectionReviewQueueApi.entities({
        ...keyParams,
        offset: pageParam,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    // Live triage queue — opt out of the 5-minute global staleTime.
    staleTime: 60_000,
  });

  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;

  // Resolve every assignee id across the visible rows in one batched lookup
  // so the Assigned-staff column renders names, never GUIDs.
  const staff = useInternalUserLabels(rows.flatMap((r) => r.assignedToUserIds));

  // ——— filter mutators (URL-persisted) ————————————————————————
  const writeParams = (mutate: (next: URLSearchParams) => void) => {
    const merged = new URLSearchParams(params);
    mutate(merged);
    setParams(merged, { replace: true });
  };
  const setParam = (key: string, value: string) =>
    writeParams((m) => {
      if (value) m.set(key, value);
      else m.delete(key);
    });

  const columns: Column<ReviewQueueEntityRow>[] = [
    {
      key: "entity",
      header: "Account",
      render: (r) => (
        <div className="flex flex-col gap-space-1">
          <span className="flex items-center gap-space-2">
            <span className="font-semibold text-onyx">{r.displayName}</span>
            <Badge status="info">{r.accountType}</Badge>
          </span>
          <span className="text-caption text-gray-mid">{r.email}</span>
        </div>
      ),
    },
    {
      key: "severity",
      header: "Status",
      className: "whitespace-nowrap",
      render: (r) => {
        const sev = rowSeverity(r);
        return <Badge status={sev.status}>{sev.label}</Badge>;
      },
    },
    {
      key: "awaiting",
      header: "Awaiting your review",
      render: (r) => (
        <CountCell row={r} tile="awaiting" count={r.awaitingReviewCount} />
      ),
    },
    {
      key: "overdue",
      header: "Overdue",
      render: (r) => (
        <CountCell
          row={r}
          tile="overdue"
          count={r.overdueCount}
          sub={
            r.oldestOverdueAgeDays != null
              ? `oldest ${r.oldestOverdueAgeDays}d`
              : undefined
          }
        />
      ),
    },
    {
      key: "pending",
      header: "Pending recipient",
      render: (r) => (
        <CountCell row={r} tile="pending" count={r.pendingRecipientCount} />
      ),
    },
    {
      key: "returned",
      header: "Returned",
      render: (r) => (
        <CountCell row={r} tile="returned" count={r.returnedCount} />
      ),
    },
    {
      key: "lastActivity",
      header: "Last activity",
      className: "whitespace-nowrap",
      render: (r) =>
        r.lastActivityAt ? (
          <time
            dateTime={r.lastActivityAt}
            title={dateTime(r.lastActivityAt)}
            className="text-small text-onyx"
          >
            {formatRelativeTime(r.lastActivityAt, tz)}
          </time>
        ) : (
          <span className="text-caption text-gray-mid italic">—</span>
        ),
    },
    {
      key: "assigned",
      header: "Assigned staff",
      render: (r) => {
        const ids = r.assignedToUserIds ?? [];
        if (ids.length === 0)
          return <span className="text-caption text-gray-mid italic">—</span>;
        return (
          <div className="flex flex-wrap gap-space-1">
            {ids.map((id) => (
              <Badge key={id} status="info">
                {staff.lookup(id)?.displayName ?? fallbackUserLabel(id)}
              </Badge>
            ))}
          </div>
        );
      },
    },
  ];

  const labelClass =
    "text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold";

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="External Collection"
        title="Review Queue"
        description="One row per portal account — triage every party's outstanding collection work and click through to its Review Queue tab."
      />

      <div className="flex flex-col gap-space-3">
        <div className="flex flex-wrap items-end gap-space-3">
          <Input
            type="search"
            placeholder="Search name or email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-64"
            aria-label="Search accounts"
          />
          <div className="w-44">
            <span className={labelClass}>Account type</span>
            <Select
              value={accountType}
              onChange={(e) => setParam("accountType", e.target.value)}
              aria-label="Account type filter"
            >
              <option value="">All types</option>
              {ACCOUNT_TYPES.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-44">
            <span className={labelClass}>Status</span>
            <Select
              value={status}
              onChange={(e) => setParam("status", e.target.value)}
              aria-label="Status filter"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-72">
            <span className={labelClass}>Assigned staff</span>
            <InternalUserPicker
              value={assignedToUserId ? [assignedToUserId] : []}
              onChange={(ids) => setParam("assignedToUserId", ids[0] ?? "")}
              placeholder="Filter by assignee…"
              maxSelections={1}
            />
          </div>
          <label className="inline-flex items-center gap-space-2 text-small text-onyx h-10">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) =>
                setParam("overdueOnly", e.target.checked ? "true" : "")
              }
            />
            Overdue only
          </label>
        </div>
        <div className="flex flex-wrap items-end gap-space-3">
          <div className="w-40">
            <span className={labelClass}>Activity from</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => setParam("from", e.target.value)}
              aria-label="Activity from date"
            />
          </div>
          <div className="w-40">
            <span className={labelClass}>Activity to</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => setParam("to", e.target.value)}
              aria-label="Activity to date"
            />
          </div>
          <div className="w-48">
            <span className={labelClass}>Sort</span>
            <Select
              value={sortBy}
              onChange={(e) => setParam("sortBy", e.target.value)}
              aria-label="Sort by"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.portalUserId}
        loading={list.isLoading}
        error={list.error instanceof Error ? list.error.message : null}
        emptyMessage="No portal accounts match the current filters."
        onRowClick={(r) => navigate(reviewQueueRowUrl(r))}
        rowClassName="[&>td]:py-space-4"
        rowTestId={(r) => `rq-row-${r.portalUserId}`}
      />

      {!list.isLoading && rows.length > 0 && (
        <LoadMoreFooter
          loadedCount={rows.length}
          total={total}
          loading={list.isFetchingNextPage}
          onLoadMore={() => list.fetchNextPage()}
        />
      )}
    </div>
  );
}
